<!-- eslint-disable no-tabs -->
<template>
  <div id="meetings-inbox">
    <div class="heading-container">
      <h1><i class="fas fa-headset" aria-hidden="true"></i>
        Meetings Inbox
      </h1>
      <div class="heading-actions">
        <template v-if="showMicrosoftConnectionUi">
          <button
            v-if="oauthStore.isProviderConnected('microsoft')"
            type="button"
            class="btn btn-outlined microsoft-connection-status"
            disabled>
            <i class="fas fa-check" aria-hidden="true"></i>
            Connected to Microsoft
          </button>
          <button
            v-else
            type="button"
            class="btn btn-outlined"
            @click="startMicrosoftOAuth">
            {{ oauthStore.providerConnectLabel('microsoft') }}
          </button>
        </template>
        <button v-if="$featureFlags.isEnabled('ad-hoc-meetings')"
          class="btn btn-primary"
          @click="showAddAdHocMeetingModal = true">
          Add Ad-Hoc Meeting <i class="fas fa-plus" aria-hidden="true"></i>
        </button>
      </div>
    </div>
    <k-nav-tabs
      :current-tab="currentTab"
      :tabs="tabs"
      :useRoutes="false"
      :useUrlQueryParams="true"
      @tab-selected="currentTab = $event">
    </k-nav-tabs>
    <div class="panel panel-default">
      <div class="panel-body" :class="{ 'no-date-filters': currentTab === 'completed' }">
        <div class="controls-container">
            <k-date-filters v-if="currentTab !== 'completed'"
              :show-next-two-weeks="true"
              :show-next-month="true"
              @filter-window-changed="onFilterWindowChanged"
            />
            <k-toggle v-model="showAllMeetings" size="small" label="Show Ad-Hoc Meetings" />
        </div>
          <k-table class="meetings-inbox-table"
                  :headers="kableHeaders"
                  :rows="filteredRows"
                  :hideable="false"
                  :panel="false"
                  :max="10"
                  :loading="!meetingsReady"
                  :sticky-header="true"
                  @clicked="handleTableAction"
                  :class-func="getRowClasses"
                  :order-by="orderByColumn"
                  :filtersClearable="true"

          ></k-table>
      </div>
    </div>
    <meeting-scheduler v-if="meetingsReady"
                      :user-id="userId"
                      :meeting="selectedMeeting"
                      :show-modal="showSchedulerModal"
                      :show-unschedule-button="currentTab === 'upcoming-meetings'"
                      @close="closeSchedulerModal()"
                      @update="getMeetings()">
    </meeting-scheduler>
    <signatures :showModal="showSignaturesModal"
                :meeting="selectedMeeting"
                @close="closeSignaturesModal()">
    </signatures>
    <assign-meeting-owner :showModal="showMeetingOwnerModal"
                          :meeting="selectedMeeting"
                          @update="getMeetings()"
                          @close="closeMeetingOwnerModal()">
    </assign-meeting-owner>
    <add-ad-hoc-meeting v-if="$featureFlags.isEnabled('ad-hoc-meetings')"
      :show-modal="showAddAdHocMeetingModal"
      @close="showAddAdHocMeetingModal = false"
      @meeting-added="getMeetings">
    </add-ad-hoc-meeting>
  </div>
</template>

<style>
#meetings-inbox .k-table-key-name a div.student-ids,
#meetings-inbox .k-table-key-name a div.learner-status,
#meetings-inbox .k-table-key-name a {
  text-decoration: none;
  color: var(--kate-type-primary);
}

#meetings-inbox .k-table-key-name a div.student-ids:hover,
#meetings-inbox .k-table-key-name a div.learner-status:hover,
#meetings-inbox .k-table-key-name a {
  color: var(--kate-type-primary);
}

#meetings-inbox .k-table-key-name a div div {
  color: var(--kate-type-link);
  text-decoration: underline;
}

#meetings-inbox .k-table-key-name a div div:hover {
  color: var(--kate-type-link-hover);
}

#meetings-inbox td.k-table-action button {
  width: auto;
  align-items: center;
}

#meetings-inbox th {
  min-width: 50px;
}

#meetings-inbox td.k-table-action.k-table-key-editForm,
#meetings-inbox td.k-table-action.k-table-key-viewSignatures,
#meetings-inbox td.k-table-action.k-table-key-fillInForm,
#meetings-inbox td.k-table-action.k-table-key-reschedule,
#meetings-inbox td.k-table-action.k-table-key-schedule,
#meetings-inbox td.k-table-action.k-table-key-markMeetingIncomplete {
  justify-content: center;
  vertical-align: middle;
}

#meetings-inbox td.k-table-action.k-table-key-editForm > div,
#meetings-inbox td.k-table-action.k-table-key-viewSignatures > div,
#meetings-inbox td.k-table-action.k-table-key-fillInForm > div,
#meetings-inbox td.k-table-action.k-table-key-reschedule > div,
#meetings-inbox td.k-table-action.k-table-key-schedule > div,
#meetings-inbox td.k-table-action.k-table-key-markMeetingIncomplete > div {
  text-align: center;
  display: flex;
  justify-content: center;
}

#meetings-inbox .date-filters-buttons-container {
  margin: 0;
}

#meetings-inbox tr.ongoing td.k-table-date span,
#meetings-inbox tr.ongoing td.k-table-datetime span {
  color: var(--kate-type-warning);
}

#meetings-inbox tr.ongoing.live td.k-table-date span,
#meetings-inbox tr.ongoing.live td.k-table-datetime span {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  align-items: center;
}

#meetings-inbox tr.deadline-passed td.k-table-date span,
#meetings-inbox tr.deadline-passed td.k-table-datetime span {
  color: var(--kate-type-danger);
}

#meetings-inbox tr.upcoming-deadline td.k-table-date span,
#meetings-inbox tr.upcoming-deadline td.k-table-datetime span {
  color: var(--kate-type-success);
}

#meetings-inbox button.remove-ad-hoc-button {
  background-color: var(--kate-danger);
  border-color: var(--kate-danger);
}
</style>

<style scoped>
.panel-body {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.panel-body.no-date-filters {
  padding-top: 30px;
}

.controls-container {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  justify-content: space-between;
  align-items: baseline;
}

.controls-container .k-toggle {
  margin: 0;
}

.heading-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.microsoft-connection-status {
  color: var(--kate-type-primary);
}
</style>

<script>
import TimeMixin from '@mixins/time-mixins';
import KNavTabs from '@base-components/k-nav-tabs.vue';
import useGlobalStore from '@stores/global';
import useOAuthStore from '@stores/oauth-store';
import KTable from '@base-components/tables/display/k-table.vue';
import KDateFilters from '@base-components/buttons/k-date-filters.vue';
import KToggle from '@base-components/forms/k-toggle.vue';
import { sortObjectArray } from '../../../utils/sort-by-object-property';
import MeetingScheduler from './components/meeting-scheduler.vue';
import Signatures from './components/signatures.vue';
import AddAdHocMeeting from './add-ad-hoc-meeting.vue';
import AssignMeetingOwner from './components/assign-meeting-owner.vue';

export default {
  name: 'meetings-inbox',
  components: {
    AssignMeetingOwner,
    Signatures,
    MeetingScheduler,
    KNavTabs,
    KTable,
    KDateFilters,
    AddAdHocMeeting,
    KToggle,
  },
  mixins: [TimeMixin],

  data() {
    return {
      currentTab: 'meetings-to-schedule',
      tabs: [
        {
          name: 'Meetings to Schedule',
          key: 'meetings-to-schedule',
        },
        {
          name: 'Upcoming Meetings',
          key: 'upcoming-meetings',
        },
        {
          name: 'In Review',
          key: 'in-review',
        },
        {
          name: 'Completed',
          key: 'completed',
        },
      ],
      meetings: [],
      meetingsReady: false,
      deletingMeeting: false,
      showSchedulerModal: false,
      showMeetingOwnerModal: false,
      showSignaturesModal: false,
      selectedMeeting: {},
      store: useGlobalStore(),
      filterWindowStart: null,
      filterWindowEnd: null,
      showAddAdHocMeetingModal: false,
      showAllMeetings: true,
      oauthStore: useOAuthStore(),
    };
  },

  beforeMount() {
    this.$Loading.start();
    this.getMeetings();
    this.getMicrosoftConnectionStatus();
  },

  watch: {
    ready() {
      if (this.ready) {
        this.$Loading.finish();
      } else {
        this.$Loading.start();
      }
    },
  },

  computed: {
    ready() {
      return this.meetingsReady && !this.deletingMeeting;
    },
    userId() {
      return this.store.userId;
    },
    showMicrosoftConnectionUi() {
      return this.$featureFlags.isEnabled('ms-teams-integration') && this.oauthStore.isProviderStatusLoaded('microsoft');
    },
    rowFilteredByTableType() {
      if (this.currentTab === 'meetings-to-schedule') {
        return this.meetings?.filter(meeting => meeting.status === 'Not Scheduled');
      }
      if (this.currentTab === 'upcoming-meetings') {
        return this.meetings?.filter(meeting => meeting.status === 'Upcoming');
      }
      if (this.currentTab === 'in-review') {
        return this.meetings?.filter(meeting => meeting.status === 'In Review');
      }
      if (this.currentTab === 'completed') {
        return this.meetings?.filter(meeting => meeting.status === 'Completed');
      }
      return [];
    },

    filteredMeetingsByToggle() {
      if (!this.showAllMeetings) {
        // Hide ad-hoc meetings
        return this.rowFilteredByTableType.filter(meeting => !meeting.is_adhoc);
      }
      return this.rowFilteredByTableType;
    },

    meetingNameHeader() {
      return {
        meetingName: {
          name: 'Meeting',
          type: 'string',
          filterable: true,
          sortable: true,
        },
      };
    },
    typeHeader() {
      return {
        type: {
          name: 'Type',
          type: 'pill',
          sortable: true,
          options: {
            type: () => 'warning',
          },
        },
      };
    },
    programmeHeader() {
      return {
        programme: {
          name: 'Programme',
          type: 'string',
          filterable: true,
          sortable: true,
        },
      };
    },
    nameHeader() {
      return {
        name: {
          name: 'Student',
          type: 'url',
          filterable: true,
          sortable: true,
        },
      };
    },
    dateHeader() {
      return {
        date: {
          name: 'Date',
          type: 'datetime',
          sortable: true,
        },
      };
    },
    linkHeader() {
      return {
        link: {
          name: 'Video Call Link',
          type: 'button',
          options: {
            preset: 'open',
            title: row => row.link,
            ariaLabel: row => `Join meeting for ${row.student_name}`,
            disabled: row => !row.link,
            callback: this.handleTableAction,
          },
        },
      };
    },
    deadlineHeader() {
      return {
        deadline: {
          name: 'Deadline',
          type: 'datetime',
          sortable: true,
          options: {
            datetimeFormat: 'MMM d, yyyy',
          },
        },
      };
    },
    changeMeetingOwnerHeader() {
      return {
        changeMeetingOwner: {
          name: 'Change Meeting Owner',
          type: 'button',
          options: {
            preset: 'user',
            title: 'Change Meeting Owner',
            ariaLabel: 'Change Meeting Owner',
            callback: this.handleTableAction,
          },
        },
      };
    },
    scheduleHeader() {
      return {
        schedule: {
          name: 'Schedule',
          type: 'button',
          options: {
            preset: 'schedule',
            title: 'Schedule Meeting',
            ariaLabel: 'Schedule Meeting',
            callback: this.handleTableAction,
          },
        },
      };
    },
    gcalEventHeader() {
      return {
        gcalEvent: {
          name: 'Calendar',
          type: 'external_url',
          displayAs: () => 'View in Calendar',
        },
      };
    },
    gcalLastSyncHeader() {
      return {
        gcalLastSync: {
          name: 'Last Sync',
          type: 'datetime',
        },
      };
    },
    rescheduleHeader() {
      return {
        reschedule: {
          name: 'Re-schedule',
          type: 'button',
          options: {
            preset: 'schedule',
            title: 'Re-schedule Meeting',
            ariaLabel: 'Re-schedule Meeting',
            callback: this.handleTableAction,
          },
        },
      };
    },
    fillInFormHeader() {
      return {
        fillInForm: {
          name: 'Start Review',
          type: 'button',
          options: {
            preset: 'edit',
            title: 'Fill in Form',
            ariaLabel: 'Fill in Form',
            callback: this.handleTableAction,
          },
        },
      };
    },
    editFormHeader() {
      return {
        editForm: {
          name: 'Edit Form',
          type: 'button',
          options: {
            preset: 'edit',
            title: 'Edit Form',
            ariaLabel: 'Edit Form',
            callback: this.handleTableAction,
          },
        },
      };
    },
    viewFormHeader() {
      return {
        viewForm: {
          name: 'View Form',
          type: 'button',
          options: {
            preset: 'view',
            title: 'View Form',
            ariaLabel: 'View Form',
            callback: this.handleTableAction,
          },
        },
      };
    },
    visibleToEmployerHeader() {
      return {
        visibleToEmployer: {
          name: 'Visible to Employer',
          type: 'boolean',
        },
      };
    },
    includeLineManagerHeader() {
      return {
        includeLineManager: {
          name: 'Include Line Manager',
          type: 'boolean',
        },
      };
    },
    viewSignaturesHeader() {
      return {
        viewSignatures: {
          name: 'View Signatures',
          type: 'button',
          options: {
            preset: 'view',
            title: 'View Signatures',
            ariaLabel: 'View Signatures',
            callback: this.handleTableAction,
          },
        },
      };
    },
    deleteAdHocMeetingHeader() {
      return {
        deleteAdHocMeeting: {
          name: 'Delete',
          type: 'button',
          options: {
            preset: 'delete',
            disabled: row => row.is_standard_programme_asset,
            title: 'Remove Ad-hoc Meeting',
            ariaLabel: 'Remove Ad-hoc Meeting',
            callback: this.handleTableAction,
          },
        },
      };
    },
    markMeetingIncompleteHeader() {
      return {
        markMeetingIncomplete: {
          name: 'Mark as Incomplete',
          type: 'button',
          options: {
            preset: 'delete',
            icon: 'fas fa-undo-alt',
            title: 'Mark Meeting as Incomplete',
            ariaLabel: 'Mark Meeting as Incomplete',
            callback: this.handleTableAction,
          },
        },
      };
    },
    undoMeetingSubmissionHeader() {
      return {
        undoMeetingSubmission: {
          name: 'Undo Submission',
          type: 'button',
          options: {
            preset: 'delete',
            icon: 'fas fa-undo-alt',
            disabled: () => this.deletingMeeting,
            title: 'Undo meeting form submissions & signatures',
            ariaLabel: 'Undo meeting form submissions & signatures',
            callback: this.handleTableAction,
          },
        },
      };
    },
    completionDateHeader() {
      return {
        completionDate: {
          name: 'Completion Date',
          type: 'datetime',
          sortable: true,
        },
      };
    },
    kableHeaders() {
      const baseHeaders = {
        ...this.meetingNameHeader,
        ...this.typeHeader,
        ...this.programmeHeader,
        ...this.nameHeader,
      };
      if (this.currentTab === 'meetings-to-schedule') {
        return {
          ...baseHeaders,
          ...this.deadlineHeader,
          ...this.scheduleHeader,
          ...this.changeMeetingOwnerHeader,
          ...this.deleteAdHocMeetingHeader,
        };
      }
      if (this.currentTab === 'upcoming-meetings') {
        const headers = {
          ...baseHeaders,
          ...this.dateHeader,
          ...this.linkHeader,
          ...this.fillInFormHeader,
          ...this.changeMeetingOwnerHeader,
          ...this.rescheduleHeader,
          ...this.visibleToEmployerHeader,
          ...(this.$featureFlags.isEnabled('meetings-inbox-gcal-integration') ? this.includeLineManagerHeader : {}),
          ...this.deleteAdHocMeetingHeader,
        };
        if (this.$featureFlags.isEnabled('meetings-inbox-gcal-integration')) {
          headers.gcalEvent = this.gcalEventHeader.gcalEvent;
          headers.gcalLastSync = this.gcalLastSyncHeader.gcalLastSync;
        }
        return headers;
      }
      if (this.currentTab === 'in-review') {
        return {
          ...baseHeaders,
          ...this.editFormHeader,
          ...this.viewSignaturesHeader,
          ...this.undoMeetingSubmissionHeader,
          ...this.visibleToEmployerHeader,
        };
      }
      return {
        ...baseHeaders,
        ...this.viewFormHeader,
        ...this.viewSignaturesHeader,
        ...this.markMeetingIncompleteHeader,
        ...this.completionDateHeader,
        ...this.visibleToEmployerHeader,
      };
    },

    kableRows() {
      return this.filteredMeetingsByToggle.map(meeting => ({
        ...meeting,
        name: {
          text: this.getNameCellDisplay(meeting),
          path: {
            name: 'dashboard_students',
            params: {
              studentId: meeting.student_id,
            },
            query: {
              programmeId: meeting.programme_id,
            },
          },
        },
        meetingName: meeting.meeting_name,
        type: !meeting.is_standard_programme_asset ? 'Adhoc' : '-',
        programme: meeting.programme_name,
        date: meeting.meeting_start,
        link: meeting.meeting_link,
        deadline: meeting.deadline,
        visibleToEmployer: meeting.share_with_employer,
        includeLineManager: meeting.include_line_manager ?? false,
        completionDate: meeting.completed_on,
        gcalEvent: meeting.google_calendar_event_link,
        gcalLastSync: meeting.last_sync_google,
      }));
    },

    filteredRows() {
      if (this.currentTab === 'completed') {
        return this.kableRows;
      }

      if (!this.filterWindowStart || !this.filterWindowEnd) {
        return this.kableRows;
      }

      return this.kableRows.filter(row => {
        const date = this.getDate(this.currentTab !== 'meetings-to-schedule' ? row.date : row.deadline);
        return date >= this.filterWindowStart && date <= this.filterWindowEnd;
      });
    },

    orderByColumn() {
      return this.currentTab === 'upcoming-meetings' ? 'date' : null;
    },
  },

  methods: {
    getRowClasses(row) {
      // Today's date - all day
      const now = new Date();
      if (row.deadline) {
        if (this.dateIsPast(row.deadline) && !this.dateIsNow(row.deadline)) {
          return 'deadline-passed';
        }
        if (this.dateIsNow(row.deadline)) {
          return 'ongoing';
        }
        if (this.dateIsNearNow(row.deadline)) {
          return 'upcoming-deadline';
        }
      }
      if (row.meeting_start) {
        if (this.dateIsPast(row.meeting_end)) {
          return 'deadline-passed';
        }
        if (this.dateWithinRange(now, row.meeting_start, row.meeting_end)) {
          return ['ongoing', 'live'];
        }
        if (this.dateIsNearNow(row.meeting_start)) {
          return 'upcoming-deadline';
        }
      }
      if (row.completionDate) {
        if (this.dateIsPast(row.completionDate) && !this.dateIsNow(row.completionDate)) {
          return 'deadline-passed';
        }
        if (this.dateIsNow(row.completionDate)) {
          return 'ongoing';
        }
        if (this.dateIsNearNow(row.completionDate)) {
          return 'upcoming-deadline';
        }
      }
      return '';
    },
    getNameCellDisplay(meeting) {
      let status = '';
      if (meeting.learner_status) {
        status = `${meeting.learner_status}${meeting.learner_status_source ? ` (${meeting.learner_status_source})` : ''}`;
      }
      let display = `<div>${meeting.student_name}</div>`;
      display += `<div class="student-ids">${this.formatIds(meeting.student_id)}</div>`;
      if (status) {
        display += `<div class="learner-status">${status}</div>`;
      }
      return display;
    },
    onFilterWindowChanged({ start, end }) {
      this.filterWindowStart = start;
      this.filterWindowEnd = end;
    },
    getMeetings() {
      this.meetingsReady = false;
      this.$logger.info('Fetch all meetings for inbox');
      this.$http.get('/api/curriculum/meetings/inbox')
        .then(response => {
          this.$logger.info('Successfully fetched all meetings for inbox');
          this.meetings = sortObjectArray(sortObjectArray(response.data, 'deadline'), 'start_date');
        })
        .catch(error => {
          this.$logger.error('Error fetching all meetings for inbox', undefined, error);
          this.$ktoast.showError(error);
        }).then(() => {
          this.meetingsReady = true;
        });
    },
    startMicrosoftOAuth() {
      // We add the microsoft_oauth query param so we can detect when we're returning from OAuth
      // which is necessary to show the toast after the OAuth flow is complete
      this.oauthStore.startSignIn('microsoft', this.$route.fullPath, { microsoft_oauth: '1' });
    },
    getMicrosoftConnectionStatus() {
      const returningFromOAuth = Boolean(this.$route.query.microsoft_oauth);
      this.oauthStore.fetchConnectionStatus('microsoft')
        .then(connected => {
          if (connected && returningFromOAuth) {
            this.$ktoast.success('Connected to Microsoft');
          }
          this.clearMicrosoftOAuthQuery();
        })
        .catch(error => {
          this.$logger.autowarn('Error fetching Microsoft connection status', undefined, error);
          this.$ktoast.showError(error);
        });
    },
    clearMicrosoftOAuthQuery() {
      if (!this.$route.query?.microsoft_oauth) {
        return;
      }
      const query = { ...this.$route.query };
      delete query.microsoft_oauth;
      this.$router.replace({ query });
    },
    allSignaturesSigned(meeting) {
      const roles = meeting?.signatures.map(signature => signature.signee_role);
      return roles.includes('learner') && roles.includes('meeting_owner');
    },
    getLearnerSignatureDate(meeting) {
      const learnerSignature = meeting?.signatures.find(signature => signature.signee_role === 'learner');
      return learnerSignature?.signature_date;
    },
    formatIds(id) {
      const idStr = id.toString();
      const numbExtraZeros = Math.max(5 - idStr.length, 0);
      return `CS-${'0'.repeat(numbExtraZeros)}${idStr}`;
    },
    handleTableAction(key, index, row, header) {
      if (header === 'link' && row.link) {
        window.open(row.link, '_blank', 'noopener,noreferrer');
      }
      if (header === 'changeMeetingOwner') {
        this.showMeetingOwnerModal = true;
        this.selectedMeeting = this.meetings.find(
          m => m.module_meeting_id === row.module_meeting_id && m.student_id === row.student_id,
        );
      }
      if (header === 'schedule' || header === 'reschedule') {
        this.showSchedulerModal = true;
        this.selectedMeeting = this.meetings.find(
          m => m.module_meeting_id === row.module_meeting_id && m.student_id === row.student_id,
        );
      }
      if (header === 'viewSignatures') {
        this.showSignaturesModal = true;
        this.selectedMeeting = this.meetings.find(
          m => m.module_meeting_id === row.module_meeting_id && m.student_id === row.student_id,
        );
      }
      if (header === 'fillInForm' || header === 'editForm' || header === 'viewForm') {
        this.$router.push({
          name: 'dash_learner_meeting_form',
          params: {
            studentId: row.student_id,
            moduleMeetingId: row.module_meeting_id,
          },
          query: {
            from: 'meetings_inbox',
          },
        });
      }
      if (header === 'signForm') {
        this.$router.push({
          name: 'dash_learner_meeting_form',
          params: {
            studentId: row.student_id,
            moduleMeetingId: row.module_meeting_id,
          },
          query: {
            from: 'meetings_inbox',
          },
        });
      }
      if (header === 'deleteAdHocMeeting') {
        this.deleteAdHocMeeting(row);
      }
      if (header === 'markMeetingIncomplete') {
        this.confirmMarkMeetingIncomplete(row);
      }
      if (header === 'undoMeetingSubmission') {
        this.confirmUndoMeetingSubmission(row);
      }
    },
    deleteAdHocMeeting(row) {
      this.deletingMeeting = true;
      this.$logger.info('Deleting ad-hoc meeting');
      this.$http.delete(`/api/curriculum/admin/meeting/${row.module_meeting_id}/ad-hoc/user/${row.student_id}`)
        .then(() => {
          this.$logger.info('Successfully deleted ad-hoc meeting');
          this.$ktoast.success('Ad-hoc meeting deleted');
        })
        .catch(error => {
          this.$logger.error('Error deleting ad-hoc meeting', undefined, error);
          this.$ktoast.showError(error);
        }).then(() => {
          this.getMeetings();
          this.deletingMeeting = false;
        });
    },
    confirmMarkMeetingIncomplete(row) {
      const meetingName = row.meetingName || row.meeting_name || 'this meeting';
      this.$ktoast.confirmToast(
        `You are about to mark <b>${meetingName}</b> as incomplete.<br><br>This will delete the learner's signature for this meeting (if it exists).`,
        'warning',
        () => {
          this.markMeetingIncomplete(row);
        },
      );
    },
    markMeetingIncomplete(row) {
      this.deletingMeeting = true;
      this.$logger.info('Marking meeting as incomplete');
      this.$http.put(`/api/curriculum/admin/meetings/${row.module_meeting_id}/users/${row.student_id}/incomplete/with-learner-signature`)
        .then(() => {
          this.$logger.info('Successfully marked meeting as incomplete');
          this.$ktoast.success('Meeting marked as incomplete');
        })
        .catch(error => {
          this.$logger.error('Error marking meeting as incomplete', undefined, error);
          this.$ktoast.showError(error);
        }).then(() => {
          this.getMeetings();
          this.deletingMeeting = false;
        });
    },
    confirmUndoMeetingSubmission(row) {
      const meetingName = row.meetingName || row.meeting_name || 'this meeting';
      this.$ktoast.confirmToast(
        `You are about to delete all meeting form submissions and signatures for <b>${meetingName}</b>.<br><br>The meeting will be moved back to the <i><b>Upcoming Meetings</b></i> tab.<br>This is permanent data loss and cannot be undone.`,
        'warning',
        () => {
          this.undoMeetingSubmission(row);
        },
      );
    },
    undoMeetingSubmission(row) {
      const meeting = this.meetings.find(
        m => m.module_meeting_id === row.module_meeting_id && m.student_id === row.student_id,
      );
      const userModuleMeetingId = meeting?.user_module_meeting_id || row.user_module_meeting_id;
      if (!userModuleMeetingId) {
        this.$ktoast.error('Could not determine meeting ID to undo submissions for');
        return;
      }
      this.deletingMeeting = true;
      this.$logger.info('Deleting meeting form submissions and signatures', { userModuleMeetingId });
      this.$Loading.start();
      this.$http.delete(`/api/curriculum/admin/meeting/${userModuleMeetingId}/submission`)
        .then(() => {
          this.$logger.info('Successfully deleted meeting form submissions and signatures', { userModuleMeetingId });
          this.$ktoast.success('Meeting form submissions and signatures deleted');
          this.getMeetings();
        })
        .catch(error => {
          this.$logger.error('Error deleting meeting form submissions and signatures', { userModuleMeetingId }, error);
          this.$ktoast.showError(error);
        }).then(() => {
          this.deletingMeeting = false;
          this.$Loading.finish();
        });
    },
    closeMeetingOwnerModal() {
      this.showMeetingOwnerModal = false;
      this.selectedMeeting = {};
    },
    closeSchedulerModal() {
      this.showSchedulerModal = false;
      this.selectedMeeting = {};
    },
    closeSignaturesModal() {
      this.showSignaturesModal = false;
    },

  },
};
</script>
